import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import {
  Box,
  Checkbox,
  CircularProgress,
  TextField,
  Typography
} from '@material-ui/core'
import { Elements, useStripe, PaymentRequestButtonElement } from '@stripe/react-stripe-js'
import { ErrorOutlineRounded, LockRounded } from '@material-ui/icons'
import clsx from 'clsx'

import { format as fmtDate } from 'date-fns'
import { LocationOnRounded, EventRounded, DirectionsCarRounded } from '@material-ui/icons'

import { GoogleLoginButton } from '../../../user/components/google-login-button'
import { addressWithPostcode } from '../../../../lib/postcode'
import { isValidUkMobile } from '../../../../lib/mobile'
import { bookPayChargeLine, chargesOnCompletion } from '../../../../lib/payment-copy'
import { embeddedCheckout } from '../../../../lib/checkout-mode'
import { payByLink } from '../../../../lib/pay-by-link'
import { washCountLabel as readWashCountLabel } from '../../../../lib/social-proof'
import { getStripe, useStripeLoadFailed } from '../../../../lib/stripe'
import { apiErrorDetails, stripeErrorDetails } from '../../../../lib/stripe-load'
import { PriceText } from '../../../../lib/price-text'
import { actions, AnalyticsService, categories } from '../../../../services/analytics'
import { settings, isDev } from '../../../../store/constants'
import {
  context,
  setIsStripeError,
  setStripeError,
  useOrdersApi,
  usePromoCodesApi,
  useSelectors,
  useUsersApi
} from '../../../../store'
import { browserHistory } from '../../../../services/history'
import { BOOKING_ROUTES, STEPS } from '../steps'

import { styles } from './styles'
import { OneCheckout } from './one-checkout'
import { PayByLinkPanel } from './pay-by-link-panel'
import { SameDayOffer } from '../../../../components/same-day-offer'
import { useSurchargeLines } from '../../../../lib/use-surcharge-lines'
import { money } from '../../../../lib/gbp'
import { customerMessage } from '../../../../lib/api-json'
import { promoFieldNotice } from '../../../../lib/promo-credit'
import { setPromoCodeRefusal } from '../../../../store/actions/promocodes'

// Once per module: makeStyles called inside a render built a new JSS sheet,
// with new class names, on every render (booking screen, 11 Oct 2026).
const useStyles = makeStyles(styles)

// Stripe's paymentRequest exposes which wallet is in play (canMakePayment keys
// and the paymentmethod event's walletName). Map those to our GA4 method
// taxonomy so payment analytics distinguish the rails, not just card-vs-wallet.
const WALLET_METHOD = {
  applePay: 'apple_pay',
  googlePay: 'google_pay',
  link: 'link',
  browserCard: 'browser_card'
}
const walletMethodOf = name => WALLET_METHOD[name] || 'wallet'


// `embedded`: rendered inside the Book & pay screen (booking-flow experiment)
// — no outer <form>, header, recap or back link; the host page owns the
// booking form and passes `canPay` (the whole booking, car location
// included, is filled in and valid, so the wallet sheet may open),
// `onBeforePay(kind)` (validate, paint the inline errors and sync into the
// store; resolves false to abort; kind is 'wallet' | 'card') and
// `addressField` (the postcode, house number and mobile fields, shown above
// both pay buttons because every payment method uses them) and
// `typedMobile`, the mobile typed there: when embedded it is THE mobile for
// both pay paths, and this panel shows no mobile field of its own.
const CheckoutFormComponent = React.memo(({ embedded = false, canPay = true, canPayCard, cardFields = null, onPathChange = null, onBeforePay = null, addressField = null, typedMobile = '', readMobile = null, unpaid = null }) => {
  const classes = useStyles()
  const { dispatch } = useContext(context)
  const [showPromoCode, setShowPromoCode] = useState(false)
  const [promoCodeInput, setPromoCodeInput] = useState('')
  const [guestError, setGuestError] = useState('')
  const [showSignIn, setShowSignIn] = useState(false)
  const [signInEmail, setSignInEmail] = useState('')
  const [signInPassword, setSignInPassword] = useState('')
  const {
    percentDiscountPrice,
    creditDiscountPrice,
    codeDiscountPrice,
    prices,
    price,
    sortedSelectedServiceIds,
    isProcessingStripe,
    isStripeError,
    stripeError,
    user,
    isUserSignedIn,
    promoCode: appliedPromoCode,
    promoCodeInfo,
    promoCodeRefusal,
    servicesPrice,
    baseService,
    config,
    date,
    address,
    postcode,
    timeSlot,
    carModel,
    carRegistration,
    congestionZone
  } = useSelectors()

  const [mobile, setMobile] = useState(user?.mobile || '')
  useEffect(() => { if (user?.mobile) setMobile(user.mobile) }, [user?.mobile])
  const [guestFirstName, setGuestFirstName] = useState('')
  const [guestLastName, setGuestLastName] = useState('')
  const [guestEmail, setGuestEmail] = useState('')
  const [ownGuestMobile, setGuestMobile] = useState('')
  // On Book & pay the mobile is typed above both buttons (Robin, 5 Oct 2026:
  // "postcode, house number and mobile as fields before tap pay button").
  const guestMobile = embedded ? typedMobile : ownGuestMobile
  // Card path is collapsed by default - tapping "Pay with card" expands
  // the form below, then submitting it triggers the Stripe Checkout redirect.
  // Apple Pay path doesn't need this state at all (wallet provides contact).
  const [cardExpanded, setCardExpanded] = useState(false)
  // WhatsApp marketing opt-in - default unchecked (explicit consent,
  // GDPR-defensible). State lives at the step level so both the wallet
  // handler and the typed-guest flow read the same value at submit time.
  const [optedInWhatsappMarketing, setOptedInWhatsappMarketing] = useState(false)
  // Terms acceptance. Pre-checked (implicit): placing the order is the act
  // of acceptance, the visible ticked box just makes the T&Cs/Privacy links
  // prominent. Default-unchecked proved to be a silent conversion killer -
  // the Pay button sat disabled with no feedback, so users who didn't notice
  // the box abandoned, leaving no trace in server logs OR analytics (a
  // disabled submit fires neither a request nor a card_payment_started
  // event). The box can still be unticked, which re-gates both pay paths.
  const [termsAccepted, setTermsAccepted] = useState(true)

  const stripeFromHook = useStripe()
  // Stripe.js failed to load, retry included: no wallet button can show, so
  // say so and point at the card button, which still works (hosted page).
  const stripeJsFailed = useStripeLoadFailed()
  const stripe = stripeFromHook
  const { handleCheckout, apiCreateExpressCheckout, handleProcessExpressCheckout } = useOrdersApi()
  const { apiUserStatus, apiUserUpdate, apiUserSignIn, apiGuestSignUp } = useUsersApi()
  const { apiApplyPromoCode } = usePromoCodesApi()

  // ============================================
  // Express Checkout (Apple Pay / Google Pay / Link)
  // The wallet sheet returns the payer's email/name/phone - so the wallet
  // path can skip the guest form entirely. Card path keeps the existing
  // Stripe Checkout redirect.
  // ============================================
  // EMBEDDED_CHECKOUT (gwmc:checkout meta): on the Book & pay screen the one
  // embedded checkout (./one-checkout.js) replaces both the legacy wallet
  // button and the hosted-Checkout card button below. Off, nothing here
  // changes.
  // PAY_BY_LINK (1 Nov 2026): the pay step with no card in it. It takes the
  // place of every pay path on this panel - the one embedded checkout, the
  // legacy wallet button, the collapsed card form and Stripe's hosted page -
  // and it is the only branch that is not about paying now. Off (production
  // until Robin flips it) nothing below changes by a byte.
  const payLink = embedded && payByLink()
  const unified = embedded && embeddedCheckout() && !payLink
  const expressEnabled = config?.EXPRESS_CHECKOUT_ENABLED === true && !unified && !payLink
  const [paymentRequest, setPaymentRequest] = useState(null)

  useEffect(() => {
    if (!expressEnabled || !stripe || !price?.intValue) return
    const pr = stripe.paymentRequest({
      country: 'GB',
      currency: 'gbp',
      total: { label: 'GoWashMyCar', amount: price.intValue },
      requestPayerName: !isUserSignedIn,
      requestPayerEmail: !isUserSignedIn,
      requestPayerPhone: !isUserSignedIn || !user?.mobile
      // No shipping address, ever (Robin, 5 Oct 2026). A wallet's address is
      // the customer's home or delivery address, often not where the car is
      // parked, and the wallet redacts it until authorisation anyway, which
      // is how orders 51796, 51802 and 51807 went out as "London W10, UK".
      // The car's location is always the postcode and house number the
      // customer typed on the booking screen; the wallet only pays.
    })
    pr.canMakePayment().then(result => {
      if (result) {
        setPaymentRequest(pr)
        // GA4 signal: wallet button is going to render. canMakePayment returns
        // which rails are usable ({applePay}/{googlePay}/{link}) — capture them
        // so we can measure uptake per wallet, not just "a wallet was shown".
        const wallets = Object.keys(result).filter(k => result[k]).map(k => WALLET_METHOD[k] || k)
        AnalyticsService.trackWalletAvailable(wallets)
      }
    })
  }, [expressEnabled, stripe, price?.intValue, isUserSignedIn, user?.mobile])

  useEffect(() => {
    if (!paymentRequest || !stripe) return
    const handler = async ev => {
      // ev.walletName tells us the actual rail (applePay/googlePay/link/
      // browserCard) — thread it through every wallet event below so failures
      // and completions are attributable per wallet.
      const method = walletMethodOf(ev.walletName)
      if (onBeforePay && !(await onBeforePay('wallet'))) {
        ev.complete('fail')
        return
      }
      AnalyticsService.trackPaymentMethodSelected(method)
      AnalyticsService.trackWalletPaymentStarted(method)
      try {
        // 1. If guest, sign up - using the wallet's contact info if the form
        //    is empty. Splits the wallet payerName into first/last best-effort.
        if (!isUserSignedIn) {
          const walletName = (ev.payerName || '').trim()
          const [walletFirst, ...walletRest] = walletName.split(/\s+/)
          const walletLast = walletRest.join(' ')
          // Capture opt-in BEFORE the network call so the analytics event
          // reflects the value the user actually consented to at click time.
          AnalyticsService.trackWhatsappMarketingOptin(optedInWhatsappMarketing, 'wallet')
          const guestUser = await apiGuestSignUp({
            email: guestEmail || ev.payerEmail,
            firstName: guestFirstName || walletFirst,
            lastName: guestLastName || walletLast,
            // The typed mobile wins; the wallet's is only a fallback off
            // the Book & pay screen, where nothing is typed.
            mobile: guestMobile || ev.payerPhone,
            optedInWhatsappMarketing
          })
          if (!guestUser?.token) {
            ev.complete('fail')
            dispatch(setIsStripeError(true))
            AnalyticsService.trackWalletPaymentFailed('guest_signup_failed', method)
            return
          }
        }

        // 2. Create draft order + SetupIntent
        const initRes = await apiCreateExpressCheckout()
        if (initRes?.status !== 'ok' || !initRes.data?.client_secret) {
          ev.complete('fail')
          // A postcode postcodes.io does not know is refused here, before any
          // card is saved: say so rather than blame the card.
          if (/postcode/i.test(initRes?.message || '')) {
            dispatch(setStripeError(initRes.message))
            AnalyticsService.trackFieldBlocked('book', 'postcode', 'postcode_not_found')
          }
          dispatch(setIsStripeError(true))
          AnalyticsService.trackWalletPaymentFailed('setup_intent_failed', method, apiErrorDetails(initRes))
          return
        }

        // 3. Confirm SetupIntent with the wallet's payment method
        const { error, setupIntent } = await stripe.confirmCardSetup(
          initRes.data.client_secret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false }
        )
        if (error) {
          ev.complete('fail')
          dispatch(setIsStripeError(true))
          AnalyticsService.trackWalletPaymentFailed('confirm_failed', method, stripeErrorDetails(error))
          return
        }
        ev.complete('success')

        // 4. Handle 3DS if required
        if (setupIntent.status === 'requires_action') {
          const { error: actionError } = await stripe.confirmCardSetup(initRes.data.client_secret)
          if (actionError) {
            dispatch(setIsStripeError(true))
            AnalyticsService.trackWalletPaymentFailed('3ds_failed', method, stripeErrorDetails(actionError))
            return
          }
        }

        // 5. Finalize order
        const finalizeRes = await handleProcessExpressCheckout(initRes.data.setup_intent_id)
        if (finalizeRes?.status === 'ok') {
          AnalyticsService.trackWalletPaymentCompleted(method)
          browserHistory.push(BOOKING_ROUTES[STEPS.FINISH])
        } else {
          dispatch(setIsStripeError(true))
          AnalyticsService.trackWalletPaymentFailed('finalize_failed', method, apiErrorDetails(finalizeRes))
        }
      } catch (err) {
        console.error('[Express checkout] error:', err)
        ev.complete('fail')
        dispatch(setIsStripeError(true))
        AnalyticsService.trackWalletPaymentFailed('exception', method, stripeErrorDetails(err))
      }
    }
    paymentRequest.on('paymentmethod', handler)
    return () => paymentRequest.off('paymentmethod', handler)
  }, [paymentRequest, stripe, apiCreateExpressCheckout, handleProcessExpressCheckout, apiGuestSignUp, dispatch, isUserSignedIn, guestEmail, guestFirstName, guestLastName, guestMobile, optedInWhatsappMarketing, onBeforePay])

  // ============================================
  // Card path (Stripe Checkout hosted redirect)
  // ============================================
  const checkout = useCallback(async () => {
    setGuestError('')
    if (onBeforePay && !(await onBeforePay('card'))) return
    // The mobile as it stands after onBeforePay, not as it was when this
    // render began: onBeforePay may have just taken it from the box (a silent
    // autofill), and `typedMobile` is only that of the last render.
    const mobileNow = embedded && readMobile ? readMobile() : guestMobile
    // Stripe.js loads on demand (lib/stripe.js); a quick tap can beat it, so
    // wait for the instance rather than reading the hook's null.
    const stripe = stripeFromHook || (await getStripe())
    // null: Stripe.js failed twice (lib/stripe.js). handleCheckout then goes
    // to Stripe's hosted page by its URL, which needs no Stripe.js.
    if (!stripe) AnalyticsService.trackStripeFallbackTapped()
    if (isUserSignedIn) {
      const newMobile = embedded ? mobileNow : mobile
      // Also when the saved one is junk ("12345678"): the typed number wins.
      if ((!user?.mobile || !isValidUkMobile(user.mobile)) && newMobile) {
        if (!isValidUkMobile(newMobile)) {
          setGuestError('Please enter a valid UK mobile number (e.g. 07766554433).')
          AnalyticsService.trackValidationBlocked('checkout', 'invalid_mobile')
          return
        }
        apiUserUpdate({ mobile: newMobile })
      }
      handleCheckout(stripe)
    } else {
      // Mobile-only guest (2026-09-17): the one thing the operator needs
      // before the wash. Name + email are collected by Stripe Checkout on
      // its own page and copied back after payment (server side), so the
      // card path is "mobile → Pay" instead of a four-field form.
      if (!mobileNow) {
        setGuestError('Please enter your mobile number so your valeter can reach you.')
        AnalyticsService.trackValidationBlocked('checkout', 'missing_guest_details')
        return
      }
      if (!isValidUkMobile(mobileNow)) {
        setGuestError('Please enter a valid UK mobile number (e.g. 07766 554433).')
        AnalyticsService.trackValidationBlocked('checkout', 'invalid_mobile')
        return
      }
      // GA4 - customer chose guest checkout (i.e. didn't sign in or use
      // Google). Fires once per real submission, after the guest form
      // passes client-side validation.
      AnalyticsService.trackSigninMethodSelected('guest')
      AnalyticsService.trackWhatsappMarketingOptin(optedInWhatsappMarketing, 'card')
      apiGuestSignUp({
        mobile: mobileNow,
        optedInWhatsappMarketing
      })
        .then(guestUser => {
          if (guestUser?.token) {
            handleCheckout(stripe)
          } else {
            setGuestError("We couldn't save your details. Please try again.")
            AnalyticsService.trackValidationBlocked('checkout', 'guest_signup_failed')
          }
        })
        .catch(err => {
          setGuestError(customerMessage(err, 'Something went wrong. Please try again.'))
          AnalyticsService.trackValidationBlocked('checkout', 'guest_signup_error')
          // The server's Phonelib check refused the typed mobile.
          if (embedded && /mobile/i.test(err?.message || '')) AnalyticsService.trackFieldBlocked('book', 'mobile', 'not_a_mobile')
        })
    }
  }, [apiGuestSignUp, apiUserUpdate, guestEmail, guestFirstName, guestLastName, guestMobile, handleCheckout, isUserSignedIn, mobile, optedInWhatsappMarketing, stripe, user, onBeforePay, embedded, typedMobile, readMobile])

  // The auto-press is gone with the panel that caused it (31 Oct 2026):
  // Book & pay asked for a missing field at the buttons and pressed Pay for
  // the customer once they answered. Nothing is missing at a press now.

  const handleSignIn = useCallback(event => {
    event.preventDefault()
    setGuestError('')
    AnalyticsService.trackSigninMethodSelected('email')
    apiUserSignIn({ email: signInEmail, password: signInPassword })
  }, [apiUserSignIn, signInEmail, signInPassword])

  const handlePromoCode = useCallback(event => {
    event.preventDefault()
    setShowPromoCode(false)
    AnalyticsService.trackEvent(categories.booking, actions.applyPromoCode, promoCodeInput)
    apiApplyPromoCode(promoCodeInput)
  }, [promoCodeInput, apiApplyPromoCode])

  // Editing the code clears what was said about the last one; the field
  // stays open while it is being corrected.
  const editPromoCode = useCallback(value => {
    setPromoCodeInput(value)
    setShowPromoCode(true)
    if (promoCodeRefusal) dispatch(setPromoCodeRefusal(null))
  }, [promoCodeRefusal, dispatch])

  // A refused code keeps the field open with the code in it, so it can be
  // corrected right there (on a phone the collapsed "Have a promo code?"
  // link was one more tap, and the field shut again on blur). A refused
  // ?promo_code= link fills the field with its code too.
  const promoFieldOpen = showPromoCode || !!promoCodeRefusal
  // An empty field shuts when left; one with a code in it stays (a late
  // blur timer shut the field mid-correction after a refusal, iPhone CI,
  // 26 Oct 2026). Applying an accepted code shuts it.
  const closePromoFieldIfEmpty = ev => {
    if (!ev.target.value) setTimeout(() => setShowPromoCode(false), 250)
  }
  useEffect(() => {
    if (promoCodeRefusal?.code && !promoCodeInput) setPromoCodeInput(promoCodeRefusal.code.toUpperCase())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoCodeRefusal])

  // A refused code said right under the code field (expired, used up,
  // unknown, not for this booker...), or a held code this basket is under
  // the floor of. Never as money off (26 Oct 2026, lib/promo-credit.js).
  const promoNotice = promoFieldNotice(promoCodeRefusal, promoCodeInfo, servicesPrice.intValue)
  const promoNoticeLine = promoNotice && (
    <p className={classes.promoNotice} role="alert" data-testid="promo-code-notice" data-reason={promoNotice.reason}>
      <ErrorOutlineRounded aria-hidden="true" />
      <span>{promoNotice.message}</span>
    </p>
  )

  const handleSubmit = ev => {
    ev.preventDefault()
    checkout()
  }

  useLayoutEffect(() => { if (!embedded) window.scrollTo(0, 0) }, [embedded])
  useEffect(() => { apiUserStatus() }, [apiUserStatus])

  // Bounce back to /booking/details if essential state is missing. Happens
  // when the user refreshes at /booking/checkout - redux resets to its
  // defaults (date: null, address: '') and the submit handler would throw
  // a RangeError on date-fns format. Catch it here instead.
  //
  // Skipped in development so we can deep-link straight to /booking/checkout
  // while iterating on this screen without filling the rest of the form
  // each time. Production / staging keep the guard.
  useEffect(() => {
    if (isDev || embedded) return
    const hasDate = date instanceof Date ? !isNaN(date.getTime()) : !!date
    if (!hasDate || !address || sortedSelectedServiceIds.length === 0) {
      browserHistory.replace(BOOKING_ROUTES[STEPS.DETAILS])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Dismiss the global Stripe-error sticky pill when the user starts editing.
  useEffect(() => { if (isStripeError) dispatch(setIsStripeError(false)) /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [guestEmail, guestFirstName, guestLastName, guestMobile])

  // Total line - service summary at the top of the card.
  const summaryLine = useMemo(() => {
    const name = baseService?.name || 'Wash'
    const extras = sortedSelectedServiceIds.length - 1
    const extrasText = extras > 0 ? ` + ${extras} extra${extras > 1 ? 's' : ''}` : ''
    return `${name}${extrasText}`
  }, [baseService?.name, sortedSelectedServiceIds.length])

  // Until /api/v2/services has answered the total is a meaningless £0.00 and
  // the base service has no name: show the price skeleton (same convention
  // as the marketing pages) instead of "Wash £0.00". The service tiles are
  // clickable before prices land, so a fast tap on a cold load gets here.
  const pricesLoaded = prices.ids.length > 0
  // The live wash count, same source and fallback as the home page
  // (api/v2/config_controller.rb -> config.TOTAL_COMPLETED_WASHES), or null
  // while SHOW_SOCIAL_PROOF is off (lib/social-proof.js).
  const washCountLabel = readWashCountLabel(config)
  const totalText = pricesLoaded ? money(price) : <PriceText value={null} />
  // The large-car and same-day charges, each its own line under the total
  // (lib/price-lines.js, 17 Oct 2026: no price surprises).
  const surcharges = useSurchargeLines()
  // Order::TimeSlots: "Same-day bookings close at 12pm. Please pick
  // tomorrow or later.", refused by the server at pay.
  const sameDayRefused = isStripeError && /Same-day bookings close/i.test(stripeError || '')
  useEffect(() => { if (sameDayRefused) AnalyticsService.trackSameDayUnavailable('too_late') }, [sameDayRefused])

  // WHAT GATES THE BUTTON, and what deliberately does not.
  //
  // Blocked: a Stripe call already in flight, the sign-in panel open, terms
  // un-ticked, and a £0 total (that only happens when /api/v2/services failed
  // to load prices, and we must never POST a £0 order).
  //
  // NOT blocked: an incomplete booking form. `embedded && !canPay` used to
  // grey this button out whenever the wash, car size, date or slot was
  // missing, with nothing on screen saying which. That is precisely the
  // failure this codebase has already paid for twice and written down twice
  // (nav-buttons.js:24-31, and the terms-checkbox note above): a dead grey
  // button gives the customer no reason for the block, and because almost
  // nobody taps a greyed button it fires NO event either, so the stall is
  // invisible in PostHog as well as unexplained on screen. It is a candidate
  // for a real share of the 88.5%, and it would never show up in the data.
  //
  // Pressing it with an incomplete form is not merely safe, it is the whole
  // design now (Robin, 31 Oct 2026): `onBeforePay` fills in the defaults and
  // returns true, the order is taken, and Aider asks for whatever is missing.
  // `!price` greys it only off the booking screen, and only because a £0
  // order must never be posted.
  const cardCtaDisabled = isProcessingStripe || showSignIn || !termsAccepted || (!embedded && !price?.intValue)
  const walletBlocked = !termsAccepted

  // The wallet sheet opens on the first tap. `canPay` was the host form's
  // verdict on itself and is always true since 31 Oct 2026, so this handler
  // never stops a tap; it is kept because the sheet has to open inside the
  // tap and this is where that decision lives.
  const onWalletClick = ev => {
    if (!embedded || canPay) return
    ev.preventDefault()
    if (onBeforePay) onBeforePay('wallet')
  }

  // Whether the card path needs the user to fill the guest form before we
  // can create their account + redirect to Stripe Checkout. Signed-in users
  // who already have a mobile on file skip the form entirely.
  const cardNeedsForm = !isUserSignedIn || !user?.mobile

  // Visual: the card "trigger" button only renders when the user has a
  // wallet AND we actually have a form to hide behind it. Signed-in
  // users with a mobile on file skip the form entirely - for them the
  // green "Pay £X with card" submit IS the trigger, so showing a
  // separate white trigger above it would be a duplicate button.
  const showCardTrigger = !payLink && paymentRequest && !cardExpanded && cardNeedsForm
  const showCardForm = !payLink && (!paymentRequest || cardExpanded || !cardNeedsForm)

  const errorPill = (isStripeError || guestError) ? (
    <div className={classes.errorPill}>
      <ErrorOutlineRounded fontSize="small" />
      {/* A customer who has just tapped a pay button and seen an
          error needs to know, first, that no money moved. The
          fallback said neither. */}
      <span>{guestError || stripeError || 'We could not save your card. Nothing has been charged. Please check the details and try again.'}</span>
      {/* Only show retry for Stripe-side failures (network/Stripe).
          Guest-form validation errors clear themselves as the user
          edits, so a button there would be redundant. */}
      {isStripeError && !isProcessingStripe && (
        <button
          type="button"
          className={classes.errorRetry}
          onClick={() => { dispatch(setIsStripeError(false)); if (!unified && !payLink) checkout() }}>
          Try again
        </button>
      )}
    </div>
  ) : null

  const Wrapper = embedded ? 'div' : 'form'
  const wrapperProps = embedded ? { className: classes.embedded } : { onSubmit: handleSubmit, className: classes.page }
  return (
    <Wrapper {...wrapperProps}>
      <div className={embedded ? undefined : classes.layout}>
        <Box className={embedded ? classes.embeddedCard : classes.card}>

          {/* Order header */}
          {!embedded && (
          <div className={classes.orderHeader}>
            <div>
              <div className={classes.orderHeaderEyebrow}>You&rsquo;re paying for</div>
              <div className={classes.orderHeaderName}>{summaryLine}</div>
            </div>
            <div className={classes.orderHeaderTotal}>{totalText}</div>
          </div>
          )}

          {/* Booking read-back. Ops manager reported being unsure whether
              the system remembered their address after the Google sign-in
              mid-flow - surface the address, slot and car here so it can
              be eyeballed before paying. */}
          {!embedded && (address || date || carModel || carRegistration) && (
            <div className={classes.bookingRecap}>
              {address && (
                <div className={classes.bookingRecapRow}>
                  <LocationOnRounded className={classes.bookingRecapIcon} fontSize="small" />
                  <span className="ph-mask">{addressWithPostcode(address, postcode)}</span>
                </div>
              )}
              {(date || timeSlot) && (
                <div className={classes.bookingRecapRow}>
                  <EventRounded className={classes.bookingRecapIcon} fontSize="small" />
                  <span>
                    {date instanceof Date && !isNaN(date) ? fmtDate(date, 'EEE d MMM') : ''}
                    {date && timeSlot ? ' · ' : ''}
                    {timeSlot}
                  </span>
                </div>
              )}
              {(carModel || carRegistration) && (
                <div className={classes.bookingRecapRow}>
                  <DirectionsCarRounded className={classes.bookingRecapIcon} fontSize="small" />
                  <span>
                    {carModel}
                    {carModel && carRegistration ? ' · ' : ''}
                    {carRegistration}
                  </span>
                </div>
              )}
              <NavLink to="/booking/details" className={classes.bookingRecapEdit}>Edit</NavLink>
            </div>
          )}

          {/* Off the booking screen the error sits at the top of the card. On
              Book & pay it sits right above the pay buttons instead (below):
              at the top of this panel it was three screens above the button a
              phone customer had just tapped, so a failed guest sign-up or
              Stripe call looked like a button that did nothing. */}
          {!embedded && errorPill}

          {/* Signed-in note (Book & pay shows its own under "Your details") */}
          {isUserSignedIn && !embedded && (
            <div className={classes.signedInNoteRow}>
              <Typography className={classes.signedInNote}>
                Signed in as <strong>{user.email}</strong>
              </Typography>
            </div>
          )}

          {/* Promo code. No tip on booking: Robin, 9 Oct 2026, "cut then for
              now" (docs/DECISIONS.md). The server still takes agent_tip, so
              tipped orders and a later after-wash tip keep working. */}
          {!embedded && (
          <section className={classes.compactRow}>
            {promoFieldOpen ? (
              <div className={classes.promoInline}>
                <div className={classes.field} style={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    placeholder="Promo code"
                    value={promoCodeInput}
                    onChange={ev => editPromoCode(ev.target.value)}
                    // The keyboard's Go / Enter applies the code. It did
                    // nothing: on Book & pay the field sits inside the booking
                    // form, whose submit is a no-op (QA, 9 Oct 2026).
                    onKeyDown={ev => { if (ev.key === 'Enter') handlePromoCode(ev) }}
                    InputProps={{ disableUnderline: true, autoCapitalize: 'characters', enterKeyHint: 'go' }}
                    onBlur={closePromoFieldIfEmpty}
                    autoFocus={showPromoCode}
                  />
                </div>
                <button type="button" className={classes.primaryGhostBtn} onClick={handlePromoCode}>Apply</button>
              </div>
            ) : (
              <button type="button" className={classes.linkButton} onClick={() => setShowPromoCode(true)}>
                {(appliedPromoCode || promoCodeInfo)?.code ? `Promo applied: ${(appliedPromoCode || promoCodeInfo).code}` : 'Have a promo code?'}
              </button>
            )}
            {promoNoticeLine}
          </section>
          )}

          {/* WhatsApp marketing opt-in - visible to BOTH wallet and card
              paths so customers see + consent before tapping any pay
              button. Default unchecked (explicit consent). Transactional
              comms (booking confirmation, "operator on the way", etc.) are
              sent regardless; this gates only marketing templates. */}
          {!embedded && (
          <label className={classes.whatsappOptinRow}>
            <Checkbox
              inputProps={{ 'data-whatsapp-optin': '' }}
              checked={optedInWhatsappMarketing}
              onChange={ev => {
                const next = ev.target.checked
                setOptedInWhatsappMarketing(next)
                AnalyticsService.trackWhatsappOptinToggled(next)
              }}
              size="small"
              color="primary"
            />
            <span className={classes.whatsappOptinLabel}>
              Send me <strong>£25 refer-a-friend rewards</strong>, exclusive offers and wash reminders on WhatsApp. Reply STOP anytime.
            </span>
          </label>
          )}

          {/* Payment section */}
          <section className={classes.payment}>
            {/* Above both buttons: the legacy buttons' postcode, house number,
                note and mobile; with the one embedded checkout only the note
                (18 Oct 2026: the wallet sheet gives the address and phone, the
                card's fields are in its section, `cardFields`). */}
            {addressField}

            {/* T&Cs checkbox - gates both pay paths. Re-introduced after a
                short period of click-wrap acceptance proved confusing for
                some customers (they didn't realise placing the order was the
                act of acceptance). */}
            {embedded && (
              <label className={classes.whatsappLine}>
                <input
                  type="checkbox"
                  data-whatsapp-optin=""
                  checked={optedInWhatsappMarketing}
                  onChange={ev => {
                    const next = ev.target.checked
                    setOptedInWhatsappMarketing(next)
                    AnalyticsService.trackWhatsappOptinToggled(next)
                  }}
                />
                <span>Send me offers and wash reminders on WhatsApp. Reply STOP any time.</span>
              </label>
            )}
            {!embedded && (
            <label className={classes.termsCheckbox}>
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={ev => setTermsAccepted(ev.target.checked)}
              />
              <span>
                I agree to the{' '}
                <NavLink to="/terms_and_conditions" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</NavLink>
                {' '}and{' '}
                <NavLink to="/privacy_policy" target="_blank" rel="noopener noreferrer">Privacy Policy</NavLink>.
              </span>
            </label>
            )}

            {/* `blockedNote` is gone (31 Oct 2026): it was the panel that
                said what a refused press was missing, and no press is
                refused. A FAILED SERVER CALL still speaks, right here, where
                a phone customer is looking: errorPill. */}
            {embedded && errorPill}
            {/* The server refused today past the same-day cutoff: offer
                WhatsApp (lib/same-day-offer.js, 17 Oct 2026). */}
            {embedded && sameDayRefused && <SameDayOffer reason="too_late" getPostcode={() => postcode} />}

            {/* The total, right above the pay buttons, on every path. On a
                phone nothing else showed it there: the sticky bar hides once
                the buttons are in view, the wallet button carries no amount
                and "Book my wash, £X" sits behind Pay by card
                (address-leavers-2026-10-11.md §1.5). Same `price` as the bar:
                size, same day, congestion, extras and promo included. */}
            {embedded && (
              <p className={classes.payTotal} data-testid="pay-total" aria-live="polite" aria-atomic="true">
                <span>Total</span> <span className={classes.payTotalValue}>{totalText}</span>
              </p>
            )}
            {/* Account credit the server will take off this booking
                (lib/credit-preview.js, 26 Oct 2026), said where the total
                is, on a phone too: "£25 credit applied". */}
            {embedded && creditDiscountPrice.subtract(codeDiscountPrice).intValue > 0 && (
              <p className={classes.payTotalLines} data-testid="pay-credit-applied" aria-live="polite">
                {`${money(creditDiscountPrice.subtract(codeDiscountPrice))} credit applied`}
              </p>
            )}
            {embedded && surcharges.length > 0 && (
              <ul className={classes.payTotalLines} data-testid="pay-total-lines">
                {surcharges.map(line => (
                  <li key={line.key}><span>{line.label}</span> <span>{line.amount}</span></li>
                ))}
              </ul>
            )}

            {/* Apple Pay / Google Pay / Link - primary CTA when supported.
                Visually + interactively disabled until terms are accepted. */}
            {!payLink && paymentRequest && (
              <>
                <div
                  className={classes.walletWrap}
                  style={walletBlocked ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
                  aria-disabled={walletBlocked}>
                  <PaymentRequestButtonElement
                    onClick={onWalletClick}
                    options={{
                      paymentRequest,
                      style: { paymentRequestButton: { type: 'default', theme: 'dark', height: '54px' } }
                    }}
                  />
                </div>
                <div className={classes.payDivider}><span>or pay by card</span></div>
              </>
            )}

            {/* PAY_BY_LINK: one action, "Book now", and nothing said about
                payment on this screen at all. */}
            {payLink && (
              <PayByLinkPanel cardFields={cardFields} onBeforePay={onBeforePay} classes={classes} />
            )}

            {/* The one embedded checkout: Apple Pay first, then the card. */}
            {unified && (
              <OneCheckout
                canPay={canPay}
                canPayCard={canPayCard ?? canPay}
                cardFields={cardFields}
                pageZone={!!congestionZone}
                onPathChange={onPathChange}
                onBeforePay={onBeforePay}
                termsAccepted={termsAccepted}
                optedInWhatsappMarketing={optedInWhatsappMarketing}
                typedMobile={typedMobile}
                amountPence={price?.intValue}
                washDate={date instanceof Date ? date : (date ? new Date(date) : null)}
                totalText={totalText}
                unpaid={unpaid}
              />
            )}

            {/* Stripe.js did not load (blocker, flaky signal): no wallet row
                can render. The card button below still reaches Stripe's own
                page, so tell the customer that instead of showing nothing. */}
            {stripeJsFailed && !unified && !payLink && (
              <div className={classes.stripeFallback} role="status" data-testid="stripe-fallback">
                <ErrorOutlineRounded fontSize="small" />
                <span>
                  Apple Pay and Google Pay couldn&rsquo;t load on this connection. You can still pay by card on
                  Stripe&rsquo;s secure page.
                </span>
              </div>
            )}

            {/* Card trigger - collapses the form behind a tap so wallet users
                never see it. Only rendered when there IS a wallet button
                AND the form isn't already expanded. */}
            {showCardTrigger && (
              <button
                type="button"
                className={classes.cardTriggerBtn}
                onClick={() => setCardExpanded(true)}>
                <LockRounded style={{ fontSize: 18, marginRight: 8 }} />
                Pay by card
              </button>
            )}

            {/* Card form - guest details + signed-in mobile field. Submit
                button at the bottom triggers the Stripe Checkout redirect. */}
            {showCardForm && (
              <div className={classes.cardForm}>
                {!embedded && cardNeedsForm && !showSignIn && !isUserSignedIn && (
                  <div>
                    <label className={classes.fieldLabel}>Mobile number</label>
                    <div className={classes.field}>
                      <TextField fullWidth placeholder="07766 554433" value={guestMobile} onChange={ev => setGuestMobile(ev.target.value)} InputProps={{ disableUnderline: true, autoComplete: 'tel', type: 'tel', inputMode: 'tel' }} />
                    </div>
                    <p className={classes.secureNote} style={{ margin: '6px 0 0', textAlign: 'left' }}>
                      So your valeter can reach you on the day. Next, Stripe&rsquo;s own secure page takes your card and
                      email.
                    </p>
                  </div>
                )}

                {cardNeedsForm && showSignIn && (
                  <>
                    <Typography className={classes.cardFormHeading}>Sign in</Typography>
                    <GoogleLoginButton title="Continue with Google" />
                    <div className={classes.divider}><span>or</span></div>
                    <div>
                      <label className={classes.fieldLabel}>Email</label>
                      <div className={classes.field}>
                        <TextField fullWidth placeholder="email@example.com" value={signInEmail} onChange={ev => setSignInEmail(ev.target.value.toLowerCase())} InputProps={{ disableUnderline: true, autoComplete: 'email', type: 'email' }} />
                      </div>
                    </div>
                    <div>
                      <label className={classes.fieldLabel}>Password</label>
                      <div className={classes.field}>
                        <TextField fullWidth type="password" value={signInPassword} onChange={ev => setSignInPassword(ev.target.value)} InputProps={{ disableUnderline: true, autoComplete: 'current-password' }} />
                      </div>
                    </div>
                    <button type="button" className={classes.primaryGhostBtn} onClick={handleSignIn}>Sign in</button>
                  </>
                )}

                {/* Signed-in user but no mobile on file - collect just that */}
                {!embedded && isUserSignedIn && !user?.mobile && (
                  <div>
                    <label className={classes.fieldLabel}>Mobile number</label>
                    <div className={classes.field}>
                      <TextField fullWidth placeholder="07766554433" value={mobile} onChange={ev => setMobile(ev.target.value)} InputProps={{ disableUnderline: true, autoComplete: 'tel', type: 'tel' }} />
                    </div>
                  </div>
                )}

                {/* THE BUTTON SAID "Pay £125.93 with card". NOTHING IS PAID.
                    `User#create_stripe_checkout_session` opens the session in
                    `mode: 'setup'`: Stripe saves the card, and
                    `Order::AcceptService#process_stripe_payment` charges it
                    only when an operator takes the job, hours later (median
                    2h16). So the old label promised a charge that does not
                    happen, on the exact tap where a customer decides whether
                    to trust us, and then handed them a Stripe page that asks
                    to save a card and shows no amount. Whatever else is wrong
                    with this funnel, a button that misdescribes what it does
                    is not something to leave running. */}
                {!unified && (
                <>
                <button
                  type={embedded ? 'button' : 'submit'}
                  onClick={embedded ? checkout : undefined}
                  className={clsx(classes.cardCta, cardCtaDisabled && classes.cardCtaDisabled)}
                  disabled={cardCtaDisabled}>
                  <LockRounded style={{ fontSize: 18, marginRight: 8 }} />
                  {isProcessingStripe ? 'Just a sec…' : (stripeJsFailed ? <>Pay by card, {totalText}</> : <>Book my wash, {totalText}</>)}
                  {isProcessingStripe && <CircularProgress size={18} className={classes.btnSpinner} />}
                </button>
                <p className={classes.ctaHelper}>
                  {embedded
                    // The one place Book & pay says when money moves (Robin, 9
                    // Oct 2026: "keep the your card is charged stuff after the
                    // green button").
                    ? bookPayChargeLine()
                    : (chargesOnCompletion()
                        ? 'You pay once your car is clean.'
                        : 'Your card will be charged shortly after your booking is made.')}
                </p>
                </>
                )}

                {/* Sign-in / guest toggles */}
                {!embedded && !isUserSignedIn && cardNeedsForm && (
                  <button
                    type="button"
                    className={classes.linkButton}
                    style={{ alignSelf: 'center', marginTop: 4 }}
                    onClick={() => setShowSignIn(s => !s)}>
                    {showSignIn ? '← Continue as guest' : 'Already have an account? Sign in'}
                  </button>
                )}
              </div>
            )}

            {/* Said "Secure payment via Stripe" and nothing about when money
                moves, which is the one thing a customer wants to know here. */}
            {!embedded && (
            <p className={classes.secureNote}>
              <LockRounded style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 4 }} />
              Your card details go straight to Stripe and never to us. We keep the card on file to make repeat
              bookings easy.
            </p>
            )}

            {/* Book & pay, under the buttons: the terms as a line (no box to
                untick and grey both buttons by accident), one trust line, and
                the promo link (most promo users arrive by ?promo_code=). */}
            {embedded && (
              <div className={classes.smallPrint}>
                <p className={classes.termsLine}>
                  By booking you agree to our{' '}
                  <NavLink to="/terms_and_conditions" target="_blank" rel="noopener noreferrer">Terms</NavLink>
                  {' '}and{' '}
                  <NavLink to="/privacy_policy" target="_blank" rel="noopener noreferrer">Privacy Policy</NavLink>.
                </p>
                <p className={classes.termsLine}>{washCountLabel && `${washCountLabel} washes since 2016. `}We bring water and power, so you don&rsquo;t need to be home.</p>
                {promoFieldOpen ? (
                  <div className={classes.promoInline}>
                    <div className={classes.field} style={{ flex: 1 }}>
                      <TextField
                        fullWidth
                        placeholder="Promo code"
                        value={promoCodeInput}
                        onChange={ev => editPromoCode(ev.target.value)}
                        onKeyDown={ev => { if (ev.key === 'Enter') handlePromoCode(ev) }}
                        InputProps={{ disableUnderline: true, autoCapitalize: 'characters', enterKeyHint: 'go' }}
                        onBlur={closePromoFieldIfEmpty}
                        autoFocus={showPromoCode}
                      />
                    </div>
                    <button type="button" className={classes.primaryGhostBtn} onClick={handlePromoCode}>Apply</button>
                  </div>
                ) : (
                  <button type="button" className={classes.linkButton} onClick={() => setShowPromoCode(true)}>
                    {(appliedPromoCode || promoCodeInfo)?.code ? `Promo applied: ${(appliedPromoCode || promoCodeInfo).code}` : 'Have a promo code?'}
                  </button>
                )}
                {promoNoticeLine}
              </div>
            )}
          </section>

          {/* Back link */}
          {!embedded && (
          <div className={classes.bottomNav}>
            <NavLink
              to={BOOKING_ROUTES[STEPS.EXTRA]}
              onClick={e => {
                e.preventDefault()
                AnalyticsService.trackEvent(categories.booking, actions.bookingStep3)
                browserHistory.push(BOOKING_ROUTES[STEPS.EXTRA])
              }}
              className={classes.backLink}>
              ← Back
            </NavLink>
          </div>
          )}
        </Box>
      </div>
    </Wrapper>
  )
})


export const StepCheckout = () => {
  return (
    <Elements stripe={getStripe()}>
      <CheckoutFormComponent />
    </Elements>
  )
}

// The payment panel on its own, for the Book & pay screen.
// With the one embedded checkout (18 Oct 2026): `canPay` is the wallet's
// verdict (taps only), `canPayCard` the card's (taps and typed fields),
// `cardFields` the card path's postcode, house number and mobile (shown in
// the card section only), `onPathChange` which path the page is asking for.
export const CheckoutPanel = ({ canPay, canPayCard, cardFields, onPathChange, onBeforePay, addressField, typedMobile, readMobile, unpaid }) => (
  <Elements stripe={getStripe()}>
    <CheckoutFormComponent embedded canPay={canPay} canPayCard={canPayCard} cardFields={cardFields} onPathChange={onPathChange} onBeforePay={onBeforePay} addressField={addressField} typedMobile={typedMobile} readMobile={readMobile} unpaid={unpaid} />
  </Elements>
)
